import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  activityTypeData,
  DocumentPreviewService,
  downloadFileAs,
  ensureAbsoluteUrl,
  getFileDownloadName,
  ToastService,
} from '@isophi/core-ui';
import { PortfolioManagerReportsService } from '@isophi/isophi-pdfs-api';
import { FeelsMoreConfidentEnum, ManagerActivity, PapCategoriesService, PortfolioService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { map, takeUntil } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { studyTypeData } from '../../constants/data';
import { PortfolioActivityDetailForm, SelfReflectionForm } from '../../constants/forms';

@Component({
  selector: 'app-pap-portfolio-detail',
  templateUrl: './pap-portfolio-detail.component.html',
  styleUrls: ['./pap-portfolio-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapPortfolioDetailComponent extends BaseComponent implements OnInit {
  @Input() portfolioActivityId: string;

  portfolioActivity: ManagerActivity;

  loadingPortfolioActivity = true;

  activityTypeData = activityTypeData;

  studyTypeData = studyTypeData;

  materialServer = environment.materialServer;

  form = new FormGroup<PortfolioActivityDetailForm>({
    description: new FormControl<string | null>(null),
    selfReflection: new FormGroup<SelfReflectionForm>({
      activityHelped: new FormControl<FeelsMoreConfidentEnum | null>(null),
      knowledgeUsage: new FormControl<FeelsMoreConfidentEnum | null>(null),
      challengeExperienced: new FormControl<FeelsMoreConfidentEnum | null>(null),
      expectationsMet: new FormControl<FeelsMoreConfidentEnum | null>(null),
      feelsMoreConfident: new FormControl<FeelsMoreConfidentEnum | null>(null),
      selfReflectionText: new FormControl<string | null>(null),
    }),
    competenceUuids: new FormControl<string[]>([]),
    userDescription: new FormControl<string | null>(null),
  });

  private readonly route = inject(ActivatedRoute);
  private readonly routerLinkFactory = inject(RouterLinkFactory);
  private readonly portfolioService = inject(PortfolioService);
  private readonly papCategoriesService = inject(PapCategoriesService);
  private readonly portfolioManagerReportsService = inject(PortfolioManagerReportsService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  protected readonly helperService = inject(HelperService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly cd = inject(ChangeDetectorRef);

  private readonly documentPreviewService = inject(DocumentPreviewService);

  fetchCategories = () =>
    this.papCategoriesService.papCategoriesList(undefined, undefined, undefined, 1000).pipe(map((categories) => categories.results));

  fetchPortfolioActivityTargetLinks = (portfolioActivityId: string) =>
    this.portfolioService.portfolioActivityTargetLinksList(portfolioActivityId).pipe(map((links) => links.results));

  fetchPortfolioTargets = () => this.portfolioService.portfolioTargetsList().pipe(map((targets) => targets.results));

  buildPortfolioTargetRouteLink = (portfolioTargetId: string) => this.routerLinkFactory.papPlanDetail(portfolioTargetId);

  organizerWebsiteHref = () => ensureAbsoluteUrl(this.portfolioActivity?.organizerWebsite);

  ngOnInit(): void {
    this.portfolioActivityId = this.route.snapshot.params['id'];
    this.loadPortfolioActivity();
  }

  loadPortfolioActivity(): void {
    this.loadingPortfolioActivity = true;
    this.portfolioService
      .portfolioActivitiesRetrieve(this.portfolioActivityId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (portfolioActivity) => {
          this.portfolioActivity = portfolioActivity;
          this.helperService.setTitle(null, portfolioActivity.title);
          this._fillPortfolioActivityForm();
          this.loadingPortfolioActivity = false;
        },
        error: () => {
          this.loadingPortfolioActivity = false;
          this.toastService.error(this.translocoService.translate('pap/portfolio/loadErrorToast'));
        },
      });
  }

  printPage(): void {
    this.spinner.show();
    this.portfolioManagerReportsService
      .getManagerActivityDetailReport({ activityId: this.portfolioActivityId })
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data: Blob) => {
          this.documentPreviewService.openBlob(data, `${this.translocoService.translate('file/activityReportDetail')}.pdf`);
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('pap/portfolio/printManagerActivityReportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  downloadDocument(documentUrl: string): void {
    const document = this.portfolioActivity?.documents?.find((d) => d.file === documentUrl);
    if (!document) {
      window.open(documentUrl, '_blank');
      return;
    }
    downloadFileAs(documentUrl, getFileDownloadName(document));
  }

  private _fillPortfolioActivityForm(): void {
    if (!this.portfolioActivity) {
      return;
    }

    this.form.patchValue({
      // FIXME: Fix openapi NullEnum annotation
      description: this.portfolioActivity.description,
      selfReflection: {
        activityHelped: this.portfolioActivity.activityHelped === 'null' ? null : this.portfolioActivity.activityHelped,
        knowledgeUsage: this.portfolioActivity.knowledgeUsage === 'null' ? null : this.portfolioActivity.knowledgeUsage,
        challengeExperienced: this.portfolioActivity.challengeExperienced === 'null' ? null : this.portfolioActivity.challengeExperienced,
        expectationsMet: this.portfolioActivity.expectationsMet === 'null' ? null : this.portfolioActivity.expectationsMet,
        feelsMoreConfident: this.portfolioActivity.feelsMoreConfident === 'null' ? null : this.portfolioActivity.feelsMoreConfident,
        selfReflectionText: this.portfolioActivity.selfReflectionText,
      },
      competenceUuids: this.portfolioActivity.competences.map((competence) => competence.uuid),
    });

    this.cd.markForCheck();
  }
}
